import React, { useState, forwardRef, useImperativeHandle, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { Drawer, Button, Steps, Form, Popover, Input, Radio, notification, Space, Select, Table, Switch, Upload, Flex, Empty, message, Tag, Badge } from 'antd';
import { LoadingOutlined, FileExcelOutlined, FileTextOutlined, ShareAltOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Resizable } from 'react-resizable';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import TradingPartnersAPI from '../../../restAPI/TradingPartnersAPI';
import DocumentManagementAPI from '../../../restAPI/DocumentManagement';
import NotificationAPI from '../../../restAPI/NotificationAPI';
import CompanyAPI from '../../../restAPI/CompanyAPI';
import validationRules from '../../../assets/validation/rules';
import validateForm from '../../../utils/FormValidation';
import excelFile from './Trading_Partner_Bulk_Upload_Sample.xlsx';

const TRADING_PARTNER_REVIEW_COLUMNS = [
  "Code",
  "Branch",
  "Name",
  "TIN Number",
  "ID Type",
  "ID Number",
  "MSIC Code",
  "Business Activity Description",
  "Address Line 1",
  "Address Line 2",
  "Address Line 3",
  "Postal Code",
  "Address City Name",
  "Address State Code",
  "Address Country Code",
  "SST Registration Number",
  "TTX Registration Number",
  "Email Address",
  "Contact Number",
  "Default Classification Code",
  "Status",
  "Errors",
];

const branchCellFromReviewRow = (rowValue) => {
  const b = rowValue?.isBranch;
  if (b === true || String(b).toLowerCase() === "true") return "TRUE";
  return "FALSE";
};

/** Flat rows as stored in `tradingPartnerBulk` → sheet rows (header + data) for XLSX */
const tradingPartnerReviewRowsToAoA = (reviewRows) => {
  const data = reviewRows.map((rowValue) => [
    rowValue.code || "",
    branchCellFromReviewRow(rowValue),
    rowValue.tradingPartnerName || "",
    rowValue.tin || "",
    rowValue.registrationType || "",
    rowValue.registrationValue || "",
    rowValue.msic || "",
    rowValue.description || "",
    rowValue.addressLine1 || "",
    rowValue.addressLine2 || "",
    rowValue.addressLine3 || "",
    rowValue.postalCode || "",
    rowValue.addressCityName || "",
    rowValue.addressStateCode || "",
    rowValue.addressCountryCode || "",
    rowValue.sst || "",
    rowValue.ttx || "",
    rowValue.emailAddress || "",
    rowValue.contactNumber || "",
    rowValue.defaultClassificationCode || "",
    rowValue.status || "",
    Array.isArray(rowValue.errors) ? rowValue.errors.join("; ") : (rowValue.errors || ""),
  ]);
  return [TRADING_PARTNER_REVIEW_COLUMNS, ...data];
};

const buildTradingPartnerReviewWorkbookBase64 = (reviewRows) => {
  const worksheetData = tradingPartnerReviewRowsToAoA(reviewRows);
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Trading Partners Review");
  return XLSX.write(workbook, { bookType: "xlsx", type: "base64" });
};

/** API `invalidRecords` item → same row shape as bulk review table / download */
const mapApiInvalidRecordToReviewRow = (rowValue) => ({
  code: rowValue.companyCode || "",
  isBranch: rowValue.isBranch,
  tradingPartnerName: rowValue.tradingPartnerName || "",
  tin: rowValue.tin || "",
  registrationType: rowValue.registrationType || "",
  registrationValue: rowValue.registrationValue || "",
  msic: rowValue.msicCode?.code || "",
  description: rowValue.msicCode?.description || "",
  addressLine1: rowValue.address?.AddressLine1 || "",
  addressLine2: rowValue.address?.AddressLine2 || "",
  addressLine3: rowValue.address?.AddressLine3 || "",
  postalCode: rowValue.address?.PostalZone || rowValue.address?.postalZone || "",
  addressCityName: rowValue.address?.City || rowValue.address?.city || "",
  addressStateCode: rowValue.address?.State?.Code || rowValue.address?.state?.code || "",
  addressCountryCode: rowValue.country?.code || "",
  sst: rowValue.sst || "",
  ttx: rowValue.ttx || "",
  emailAddress: rowValue.emailAddress || "",
  contactNumber: rowValue.contactNumber || "",
  defaultClassificationCode: rowValue.classificationCode || "",
  status: "Fail",
  errors: rowValue.errors || [],
});

const popoverContentTIN = (
  <div>
    If TIN No is not available, please
    use: <br />
    EI00000000010: General Public <br />
    EI00000000020: Foreign Buyer<br />
    EI00000000030: Foreign Supplier<br />
    EI00000000040: Government Entity
  </div>
);

const popoverContentReg = (
  <div>
    For individuals,
    select and input the NRIC, <br />Passport
    number or Army number.
  </div>
);
const popoverContentMSIC = (
  <div>
    Refer to link below to
    obtain the correct code:<br />
    <a href="https://sdk.myinvois.hasil.gov.my/codes
/msic-codes/" target="_blank">
      https://sdk.myinvois.hasil.gov.my/codes
      /msic-codes</a><br />
    You can use "00000" if Not Applicable.
  </div>
);

const Context = React.createContext({
  name: 'Default',
});
const { Step } = Steps;
const { TextArea } = Input;
const onSearch = (value) => {
  console.log('search:', value);
};

const ResizableTitle = (props) => {
  const { onResize, width, children, ...restProps } = props;
  if (!width) return <th {...restProps}>{children}</th>;

  return (
    <th {...restProps}>
      <Resizable
        width={width}
        height={0}
        onResize={onResize}
        draggableOpts={{ enableUserSelectHack: false }}
        handle={
          <span
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              cursor: "col-resize",
              width: "2px",
              height: "100%",
              background: "#ffeb3b3b",
            }}
          />
        }
      >
        <div>{children}</div>
      </Resizable>
    </th>
  );
};

const StepForm = forwardRef(({ onRefresh }, ref) => {
  const tableContainerRef = useRef();
  const [currentStep, setCurrentStep] = useState(0);
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector(state => state.auth);
  const [countryCodes, setCountryCodes] = useState([]);
  const [iSGetCountryCodes, setISGetCountryCodes] = useState(true);
  const [stateCodes, setStateCodes] = useState([]);
  const [iSGetStateCodes, setISGetStateCodes] = useState(true);
  const [msicCodes, setMsicCodes] = useState([]);
  const [classification, setclassification] = useState([]);
  const [iSGetMsicCodes, setISGetMsicCodes] = useState(true);
  const [registrationNumberFieldDisable, setRegistrationNumberFieldDisable] = useState(false);
  const [payload, setPayload] = useState({
    "_id": "",
    "tradingPartnerName": "",
    "tin": "",
    "registrationType": "",
    "registrationValue": "",
    "sst": "",
    "ttx": "",
    "msicCode": "",
    "msicDescription": "",
    "countryCode": "",
    "countryName": "",
    "status": 1,
    "email": "",
    "contactNumber": "",
    "addressLine1": "",
    "addressLine2": "",
    "addressLine3": "",
    "postalZone": "",
    "city": "",
    "stateName": "",
    "stateCode": "",
    "groupId": "",
    "classificationCode": "",
    "companyCode": "",
    "branch": false
  });
  const [bu_payload, setBU_Payload] = useState({
    "file": {},
    "isFileUpload": false
  });
  const [tradingPartnerBulk, setTradingPartnerBulk] = useState([]);
  const notificationShown = useRef(false);
  const [errors, setErrors] = useState({});
  const [isFocused, setIsFocused] = useState(false);
  const [isDragFile, setIsDragFile] = useState(true);
  const [isTPCompanyCodeRequired, setIsTPCompanyCodeRequired] = useState(false);
  const [componentKey, setComponentKey] = useState(0); // Key for forcing re-render of the file dragger
  const [loading, setLoading] = useState(false); // for loading effect
  
  // Share functionality state
  const [shareVisible, setShareVisible] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [toInputValue, setToInputValue] = useState("");
  const [ccInputValue, setCcInputValue] = useState("");
  const [bccInputValue, setBccInputValue] = useState("");
  const [shareForm] = Form.useForm();

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openDrawer: () => {
      setVisible(true); // Open drawer
    },
    closeDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  const handleUploadSuccess = () => {
    // Update the key to reset the Dragger component
    setComponentKey((prevKey) => prevKey + 1);
  };

  const initialColumns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      fixed: "left",
      percentageWidth: 0.08,
      sorter: (a, b) => (a.code || "").localeCompare(b.code || ""),
      render: (text) => text || "",
    },
    {
      title: "Branch",
      dataIndex: "isBranch",
      key: "isBranch",
      percentageWidth: 0.08,
      render: (isBranch) => isBranch ? "TRUE" : "FALSE",
      sorter: (a, b) => (a.isBranch ? 1 : 0) - (b.isBranch ? 1 : 0),
    },
    {
      title: "Name",
      dataIndex: "tradingPartnerName",
      key: "tradingPartnerName",
      fixed: "left",
      percentageWidth: 0.15,
      sorter: (a, b) => a.tradingPartnerName.localeCompare(b.tradingPartnerName),
      render: (text) => text || "",
    },
    {
      title: "TIN Number",
      dataIndex: "tin",
      key: "tin",
      percentageWidth: 0.12,
      sorter: (a, b) => a.tin.localeCompare(b.tin),
      render: (text) => text || "",
    },
    {
      title: "ID Type",
      dataIndex: "registrationType",
      key: "registrationType",
      percentageWidth: 0.12,
      sorter: (a, b) => a.registrationType.localeCompare(b.registrationType),
      render: (text) => text || "",
    },
    {
      title: "ID Number",
      dataIndex: "registrationValue",
      key: "registrationValue",
      percentageWidth: 0.12,
      sorter: (a, b) => a.registrationValue.localeCompare(b.registrationValue),
      render: (text) => text || "",
    },
    {
      title: "MSIC Code",
      dataIndex: "msic",
      key: "msic",
      percentageWidth: 0.10,
      sorter: (a, b) => a.msic.localeCompare(b.msic),
      render: (text) => text || "",
    },
    {
      title: "Business Activity Description",
      dataIndex: "description",
      key: "description",
      percentageWidth: 0.20,
      render: (text) => text || "",
    },
    {
      title: "Address Line 1",
      dataIndex: "addressLine1",
      key: "addressLine1",
      percentageWidth: 0.15,
      render: (text) => text || "",
    },
    {
      title: "Address Line 2",
      dataIndex: "addressLine2",
      key: "addressLine2",
      percentageWidth: 0.12,
      render: (text) => text || "",
    },
    {
      title: "Address Line 3",
      dataIndex: "addressLine3",
      key: "addressLine3",
      percentageWidth: 0.12,
      render: (text) => text || "",
    },
    {
      title: "Postal Code",
      dataIndex: "postalCode",
      key: "postalCode",
      percentageWidth: 0.10,
      render: (text) => text || "",
    },
    {
      title: "Address City Name",
      dataIndex: "addressCityName",
      key: "addressCityName",
      percentageWidth: 0.12,
      render: (text) => text || "",
    },
    {
      title: "Address State Code",
      dataIndex: "addressStateCode",
      key: "addressStateCode",
      percentageWidth: 0.12,
      render: (text) => text || "",
    },
    {
      title: "Address Country Code",
      dataIndex: "addressCountryCode",
      key: "addressCountryCode",
      percentageWidth: 0.12,
      render: (text) => text || "",
    },
    {
      title: "SST Registration Number",
      dataIndex: "sst",
      key: "sst",
      percentageWidth: 0.12,
      sorter: (a, b) => a.sst.localeCompare(b.sst),
      render: (text) => text || "",
    },
    {
      title: "TTX Registration Number",
      dataIndex: "ttx",
      key: "ttx",
      percentageWidth: 0.12,
      sorter: (a, b) => a.ttx.localeCompare(b.ttx),
      render: (text) => text || "",
    },
    {
      title: "Email Address",
      dataIndex: "emailAddress",
      key: "emailAddress",
      percentageWidth: 0.15,
      sorter: (a, b) => a.emailAddress.localeCompare(b.emailAddress),
      render: (text) => text || "",
    },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
      percentageWidth: 0.12,
      sorter: (a, b) => a.contactNumber.localeCompare(b.contactNumber),
      render: (text) => text || "",
    },
    {
      title: "Default Classification Code",
      dataIndex: "defaultClassificationCode",
      key: "defaultClassificationCode",
      percentageWidth: 0.15,
      render: (text) => text || "",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      percentageWidth: 0.10,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => text || "",
    },
    {
      title: "Errors",
      dataIndex: "errors",
      key: "errors",
      fixed: 'right',
      percentageWidth: 0.25,
      render: (errors) => (
        <ul>
          {errors?.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      ),
    }
  ];

  const GetClassificationCodes = async () => {
    //const companyId = "6787e74b5035b0a86e7793c5"; // change this in release 
    await TradingPartnersAPI.GetClassificationCodes(token, localStorage.getItem('groupId'), /*companyId*/ localStorage.getItem('companyId'))
      .then((res) => {
        if (res?.data) {
          const classification = res.data.map((rowValue) => ({
            value: rowValue.code,
            label: rowValue.code
          })).sort((a, b) => a.label.localeCompare(b.label));
          const NAclassification = {
            value: "N/A",
            label: "N/A"
          };
          // Add "N/A" option at the beginning of the classification array
          classification.unshift(NAclassification);
          setclassification(classification);
        }
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon('error', 'No Document Type found', `${err.response.data.message}`, 'bottomLeft');
        } else {
          openNotificationWithIcon('error', 'No Document Type found', `${err.message}`, 'bottomLeft');
        }
      });
  }

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    if (!notificationShown.current) {
      notification[type]({
        message: message,
        description: description,
        placement,
      });
      notificationShown.current = true;
      setTimeout(() => {
        notificationShown.current = false;
      }, 5000);
    }
  };

  // Validate All mandetory fields
  const checkMandatoryValidations = () => {
    const validateData = {
      tradingPartnerName: payload.tradingPartnerName,
      country: payload.countryName,
      country: payload.countryCode,
      tin: payload.tin,
      sst: payload.sst,
      ttx: payload.ttx,
      registrationType: payload.registrationType,
      registrationValue: payload.registrationValue,
      msicCode: payload.msicCode,
      businessActivityDescription: payload.msicDescription,
    }
    
    // Add Company Code validation if required
    if (isTPCompanyCodeRequired) {
      validateData.companyCode = payload.companyCode;
    }
    
    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
        newErrors[value] = validationErr[value];
      })
      setErrors(newErrors);
      return false;
    } else {
      return true;
    }
  }

  const next = () => {
    const validationStatus = checkMandatoryValidations();
    if (validationStatus) {
      getLhdnTinNumberValidation();
    }
  }

  const prev = () => setCurrentStep(currentStep - 1);

  // Function to fetch company details and check isTPCompanyCodeRequired
  const getCompanyDetails = async () => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (companyId) {
        const response = await CompanyAPI.getCompanyById(token, localStorage.getItem('groupId'), companyId, companyId);
        if (response?.data) {
          setIsTPCompanyCodeRequired(response.data.isTPCompanyCodeRequired || false);
        }
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  useEffect(() => {
    GetClassificationCodes();
    getCompanyDetails(); // Fetch company details to check isTPCompanyCodeRequired
    if (iSGetCountryCodes) {
      getCountryCodes();
      setISGetCountryCodes(false);
    }

    if (iSGetMsicCodes) {
      getMsicCodes();
      setISGetMsicCodes(false);
    }
  }, []);
  useEffect(() => {
    console.log(JSON.stringify(payload))
  }, [payload]);

  const getCountryCodes = async () => {
    await TradingPartnersAPI.getCountryCodes(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
      if (res?.data) {
        const rows = [];
        res.data.map((rowValue) => {
          rows.push({ value: rowValue.code, label: rowValue.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') })
        });
        rows.sort((a, b) => a.label.localeCompare(b.label));
        const malaysiaIndex = rows.findIndex(item => item.value.toUpperCase() === "MYS");
        const [malaysia] = rows.splice(malaysiaIndex, 1);
        rows.unshift(malaysia);
        setCountryCodes(rows);

      }
    }).catch(err => {
      console.log("error getting contry codes data :", err.message);
    })
  };

  const getStateCodes = async () => {
    await TradingPartnersAPI.getStateCodes(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
      if (res?.data) {
        const rows = [];
        res.data.map((rowValue) => {
          rows.push({ value: rowValue.code, label: rowValue.state })
        });
        setStateCodes(rows);
      }
      else {
        setStateCodes([]);
      }
    }).catch(err => {
      console.log("error getting state codes data :", err.message);
    })
  };

  const getMsicCodes = async () => {
    await TradingPartnersAPI.getMsciCodes(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
      if (res?.data) {
        const rows = [];
        res.data.sort((a, b) => a.code.localeCompare(b.code));
        res.data.map((rowValue) => {
          rows.push({
            value: rowValue.code,
            label: `${rowValue.code} - ${rowValue.description}`, //string for search
            code: rowValue.code,
            description: rowValue.description,
          });
        });
        setMsicCodes(rows);
      }
    }).catch(err => {
      console.log("error getting msic codes data :", err.message);
    })
  };

  const getLhdnTinNumberValidation = async () => {
    await TradingPartnersAPI.getLhdnTinNumberValidation(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), payload.tin, payload.registrationType, payload.registrationValue).then((res) => {
      if (res) {
        setCurrentStep(currentStep + 1);
      }
    }).catch(err => {
      const newErrors = { "tin": "TIN is invalid" };
      setErrors(prev => ({ ...prev, ...newErrors }));
    })
  }

  const OnStateChange = (value) => {
    let state = stateCodes.filter(function (obj) {
      return obj.value === value
    });
    if (state.length > 0) {
      state.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "tradingPartnerName": payload.tradingPartnerName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": rowValue.label,
          "stateCode": rowValue.value,
          "groupId": localStorage.getItem('groupId'),
          "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
          "companyCode": payload.companyCode
        });
      });
      setErrors({});
    }
  }

  const OnCountryChange = (value) => {
    let contry = countryCodes.filter(function (obj) {
      return obj.value === value
    });

    if (contry.length > 0) {
      contry.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "tradingPartnerName": payload.tradingPartnerName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": rowValue.value,
          "countryName": rowValue.label,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
          "companyCode": payload.companyCode
        });
      });
      setErrors({});
    }
    if (value === "MYS") {
      getStateCodes();
      setISGetStateCodes(false);
      setPayload((prev) => ({
        ...prev,
        stateName: "",
        stateCode: ""
      }));
    }
    else {
      setStateCodes([]);
      setPayload((prev) => ({
        ...prev,
        stateName: "Not Applicable",
        stateCode: "17"
      }));
    }
  }

  const OnRegistrationTypeChange = async (value) => {

    const genaratedId = await generateCustomID();

    const registrationNumberValue =
      value === "FOREIGN ENTITY" || value === "GOV ENTITY"
        ? genaratedId
        : "";

    setRegistrationNumberFieldDisable(false);

    setPayload({
      "_id": payload._id,
      "tradingPartnerName": payload.tradingPartnerName,
      "tin": payload.tin,
      "registrationType": value,
      "registrationValue": registrationNumberValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": payload.msicCode,
      "msicDescription": payload.msicDescription,
      "countryCode": payload.countryCode,
      "countryName": payload.countryName,
      "status": payload.status,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "addressLine1": payload.addressLine1,
      "addressLine2": payload.addressLine2,
      "addressLine3": payload.addressLine3,
      "postalZone": payload.postalZone,
      "city": payload.city,
      "stateName": payload.stateName,
      "stateCode": payload.stateCode,
      "groupId": localStorage.getItem('groupId'),
      "classificationCode": payload.classificationCode ?? "N/A",
      "companyCode": payload.companyCode
    });
    setErrors({});
  }

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
      const err = validationResult.errors
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      })
      setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
      const keyData = Object.keys(data)[0]
      setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
  }

  const onRadioChange = (e) => {
    setPayload({
      "_id": payload._id,
      "tradingPartnerName": payload.tradingPartnerName,
      "tin": payload.tin,
      "registrationType": payload.registrationType,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": payload.msicCode,
      "msicDescription": payload.msicDescription,
      "countryCode": payload.countryCode,
      "countryName": payload.countryName,
      "status": e.target.value,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "addressLine1": payload.addressLine1,
      "addressLine2": payload.addressLine2,
      "addressLine3": payload.addressLine3,
      "postalZone": payload.postalZone,
      "city": payload.city,
      "stateName": payload.stateName,
      "stateCode": payload.stateCode,
      "groupId": localStorage.getItem('groupId'),
      "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
      "companyCode": payload.companyCode
    });
  };

  const OnMsicChange = (value) => {
    let msic = msicCodes.filter(function (obj) {
      return obj.value === value
    });

    if (msic.length > 0) {
      msic.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "tradingPartnerName": payload.tradingPartnerName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": rowValue.value,
          "msicDescription": rowValue.description,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
          "companyCode": payload.companyCode
        });
      });
      setErrors({});
    }
  }

  const validateBeforeSubmit = () => {
    const validateData = {
      tradingPartnerName: payload.tradingPartnerName,
      country: payload.countryName,
      country: payload.countryCode,
      tin: payload.tin,
      sst: payload.sst,
      ttx: payload.ttx,
      registrationType: payload.registrationType,
      registrationValue: payload.registrationValue,
      msicCode: payload.msicCode,
      businessActivityDescription: payload.msicDescription,
      tpemail: payload.email,
      contactNumber: payload.contactNumber,
      addressLine1: payload.addressLine1,
      addressLine2: payload.addressLine2,
      addressLine3: payload.addressLine3,
      postalZone: payload.postalZone,
      city: payload.city,
      state: payload.stateName,
      state: payload.stateCode,
    }
    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
        newErrors[value] = validationErr[value];
      })
      setErrors(newErrors);
      return false;
    } else {
      return true;
    }
  }

  const onSubmit = async () => {
    const validationStatus = validateBeforeSubmit();
    if (validationStatus) {
      setLoading(true); // start loading
      const formData = {
        "tradingPartnerName": payload.tradingPartnerName,
        "tin": payload.tin,
        "registrationType": payload.registrationType,
        "registrationValue": payload.registrationValue,
        "sst": payload.sst,
        "ttx": payload.ttx,
        "msicCode": {
          "code": payload.msicCode,
          "description": payload.msicDescription,
        },
        "country": {
          "code": payload.countryCode,
          "name": payload.countryName,
        },
        "status": payload.status,
        "emailAddress": payload.email,
        "contactNumber": payload.contactNumber,
        "address": {
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "state": {
            "name": payload.stateName,
            "code": payload.stateCode,
          }
        },
        "groupId": localStorage.getItem('groupId'),
        "classificationCode": payload.classificationCode,
        "isBranch": payload.branch
      };
      
      // Only include companyCode if it has a value
      if (payload.companyCode && payload.companyCode.trim() !== '') {
        formData.companyCode = payload.companyCode;
      }
      await TradingPartnersAPI.createTradingPartner(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), formData)
        .then((response) => {
          onClose();
          openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon('error', 'Trading partner creation failed', `${err.response.data.message}`, 'bottomLeft');
          } else {
            openNotificationWithIcon('error', 'Trading partner creation failed', `${err.message}`, 'bottomLeft');
          }
        }).finally(() => {
          setLoading(false); // stop loading
        });
    }
  }

  const onClose = () => {
    setVisible(false);
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    setPayload({
      "_id": "",
      "tradingPartnerName": "",
      "tin": "",
      "registrationType": "",
      "registrationValue": "",
      "sst": "",
      "ttx": "",
      "msicCode": "",
      "msicDescription": "",
      "countryCode": "",
      "countryName": "",
      "status": 1,
      "email": "",
      "contactNumber": "",
      "addressLine1": "",
      "addressLine2": "",
      "addressLine3": "",
      "postalZone": "",
      "city": "",
      "stateName": "",
      "stateCode": "",
      "groupId": "",
      "classificationCode": "",
      "companyCode": ""
    });
    setErrors({});
    setCurrentStep(0);
    setBU_Payload({
      "file": {},
      "isFileUpload": false
    });
    setTradingPartnerBulk([]);
    setIsBulkUpload(false);
    setIsDragFile(true);
  }
  // Handle input changes
  const handleInputChange = (field, value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
  };
  const handleSelectChange = (field) => (value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
  };

  const onClearChange = () => {
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    if (currentStep == 0) {
      setPayload({
        "_id": payload._id,
        "tradingPartnerName": "",
        "tin": "",
        "registrationType": "",
        "registrationValue": "",
        "sst": "",
        "ttx": "",
        "msicCode": "",
        "msicDescription": "",
        "countryCode": "",
        "countryName": "",
        "status": 1,
        "email": payload.email,
        "contactNumber": payload.contactNumber,
        "addressLine1": payload.addressLine1,
        "addressLine2": payload.addressLine2,
        "addressLine3": payload.addressLine3,
        "postalZone": payload.postalZone,
        "city": payload.city,
        "stateName": payload.stateName,
        "stateCode": payload.stateCode,
        "groupId": payload.groupId,
        "classificationCode": "",
        "companyCode": ""
      });
    } else if (currentStep == 1) {
      setPayload({
        "_id": payload._id,
        "tradingPartnerName": payload.tradingPartnerName,
        "tin": payload.tin,
        "registrationType": payload.registrationType,
        "registrationValue": payload.registrationValue,
        "sst": payload.sst,
        "ttx": payload.ttx,
        "msicCode": payload.msicCode,
        "msicDescription": payload.msicDescription,
        "countryCode": payload.countryCode,
        "countryName": payload.countryName,
        "status": 1,
        "email": "",
        "contactNumber": "",
        "addressLine1": "",
        "addressLine2": "",
        "addressLine3": "",
        "postalZone": "",
        "city": "",
        "stateName": "",
        "stateCode": "",
        "groupId": ""
      });
    }
    setErrors({});
  }

  // Compute an initial container width using window.innerWidth as fallback.
  const initialContainerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  // Set initial column widths based on the fallback container width.
  const [columns, setColumns] = useState(
    initialColumns.map(col => ({
      ...col,
      width: Math.floor(initialContainerWidth * col.percentageWidth),
    }))
  );

  // Function to update column widths based on the measured container.
  const updateColumnWidths = useCallback(() => {
    if (tableContainerRef.current) {
      const containerWidth = tableContainerRef.current.offsetWidth;
      setColumns(initialColumns.map(col => ({
        ...col,
        width: Math.floor(containerWidth * col.percentageWidth),
      })));
    }
  }, [initialColumns]);

  // Use useLayoutEffect to update column widths as soon as the component is mounted.
  useLayoutEffect(() => {
    updateColumnWidths();
  }, [updateColumnWidths]);

  // Update widths on window resize.
  useEffect(() => {
    window.addEventListener('resize', updateColumnWidths);
    return () => window.removeEventListener('resize', updateColumnWidths);
  }, [updateColumnWidths]);


  const handleResize = useCallback(
    (index) => (e, { size }) => {
      setColumns((prevColumns) => {
        const newColumns = [...prevColumns];
        newColumns[index] = { ...newColumns[index], width: size.width };
        return newColumns;
      });
    },
    []
  );

  const mergedColumns = useMemo(
    () =>
      columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column) => ({
          width: column.width,
          onResize: handleResize(index),
        }),
      })),
    [columns, handleResize]
  );
  const generateCustomID = async () => {
    const date = new Date();

    // Format date to YYMMDD
    const yy = String(date.getFullYear()).slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    const datePart = `${yy}${mm}${dd}`;

    // Generate 6 random digits
    const randomPart = Math.floor(100000 + Math.random() * 9000); // ensures 6 digits

    return `${datePart}${randomPart}`;
  };

  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <Form layout="vertical">
          {/* <Alert message="* Fields are required" type="warning" /> */}

          <Form.Item label="Trading Partner Name" validateStatus={errors.tradingPartnerName ? 'error' : ''} help={errors.tradingPartnerName || ''} required>
            <Input size="large" name="tradingPartnerName" value={payload["tradingPartnerName"]} onChange={(e) => { setPayload(prev => ({ ...prev, tradingPartnerName: e.target.value })); const validateData = { tradingPartnerName: e.target.value }; validateFormData(validateData); }} placeholder="Trading Partner Name" />
          </Form.Item>

          <Form.Item label="Country" validateStatus={errors.country ? 'error' : ''} help={errors.country || ''} required>
            <div
              style={{
                cursor: isFocused ? "text" : "pointer",
              }}
            >
              <Select
                showSearch
                name='country'
                placeholder="Select country"
                value={payload["countryCode"] || "Select a Country"}
                optionFilterProp="label"
                onChange={OnCountryChange}
                size='large'
                options={countryCodes}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              />
            </div>
          </Form.Item>

          <Form.Item 
            label="Company Code" 
            validateStatus={errors.companyCode ? 'error' : ''} 
            help={errors.companyCode || ''}
            required={isTPCompanyCodeRequired}
          >
            <Input 
              size="large" 
              name="companyCode" 
              placeholder="Company Code" 
              onChange={(e) => handleInputChange("companyCode", e.target.value)} 
              value={payload["companyCode"]} 
              required={isTPCompanyCodeRequired}
            />
          </Form.Item>
          <Form.Item label="Classification Code" >
            <Select
              showSearch
              name="classificationCode"
              placeholder="Select Classification Code"
              optionFilterProp="label"
              value={payload["classificationCode"] || "Select a Classification Code"}
              onSelect={handleSelectChange("classificationCode")}
              size='large'
              options={classification}
            />
          </Form.Item>

          <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Registration Type
              <Popover content={popoverContentReg} title="Registration Type" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } validateStatus={errors.registrationType ? 'error' : ''} help={errors.registrationType || ''} required>
            <Select
              showSearch
              name="registrationType"
              placeholder="Select Registration Type"
              optionFilterProp="label"
              value={payload["registrationType"] || "Select a Registration Type"}
              onChange={OnRegistrationTypeChange}
              size='large'
              onSearch={onSearch}
              options={[
                {
                  value: 'NRIC',
                  label: 'NRIC',
                },
                {
                  value: 'Passport number',
                  label: 'Passport number',
                },
                {
                  value: 'Business registration number',
                  label: 'Business registration number',
                },
                {
                  value: 'Army number',
                  label: 'Army number',
                },
                {
                  value: 'FOREIGN ENTITY',
                  label: 'Foreign entity',
                },
                {
                  value: 'GOV ENTITY',
                  label: 'Gov entity',
                },
              ]} />
          </Form.Item>

          <Form.Item label="Registration Number" validateStatus={errors.registrationValue ? 'error' : ''} help={errors.registrationValue || ''} required>
            <Input size="large" disabled={registrationNumberFieldDisable} name="registrationValue" value={payload["registrationValue"]} onChange={(e) => { setPayload(prev => ({ ...prev, registrationValue: e.target.value })); const validateData = { registrationValue: e.target.value }; validateFormData(validateData); }} placeholder="Registration Value" />
          </Form.Item>

          <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              TIN
              <Popover content={popoverContentTIN} title="TIN" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } validateStatus={errors.tin ? 'error' : ''} help={errors.tin || ''} required>
            <Input size="large" name="tin" value={payload["tin"]} onChange={(e) => { setPayload(prev => ({ ...prev, tin: e.target.value })); const validateData = { tin: e.target.value }; validateFormData(validateData); }} placeholder="TIN" />
          </Form.Item>

          <Form.Item label="SST" validateStatus={errors.sst ? 'error' : ''} help={errors.sst || ''} required>
            <Input size="large" name="sst" value={payload["sst"]} onChange={(e) => { setPayload(prev => ({ ...prev, sst: e.target.value })); const validateData = { sst: e.target.value }; validateFormData(validateData); }} placeholder="SST" />
          </Form.Item>

          <Form.Item label="TTX" validateStatus={errors.ttx ? 'error' : ''} help={errors.ttx || ''} required>
            <Input size="large" name="ttx" value={payload["ttx"]} onChange={(e) => { setPayload(prev => ({ ...prev, ttx: e.target.value })); const validateData = { ttx: e.target.value }; validateFormData(validateData); }} placeholder="TTX" />
          </Form.Item>

          <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              MSIC Code
              <Popover content={popoverContentMSIC} title="MSIC Code" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } validateStatus={errors.msicCode ? 'error' : ''} help={errors.msicCode || ''} required>
            <Select
              showSearch
              name="msicCode"
              placeholder="Select MSIC Code"
              optionFilterProp="label"
              value={payload["msicCode"] || "Select a MSIC Code"}
              onChange={OnMsicChange}
              size='large'
              options={msicCodes}
              optionRender={(option) => (
                <div
                  style={{
                    whiteSpace: "normal",
                    lineHeight: "1.5em",
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                  }}
                >
                  <strong>{option.data.code}</strong> - {option.data.description}
                </div>
              )}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item label="Status" required>
            <Radio.Group name="status" value={payload["status"]} onChange={onRadioChange}>
              <Radio value={1}>Active</Radio>
              <Radio value={0}>Inactive</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Branch
                <Popover 
                  content="If this is active this trading partner will be consider as branch under the trading partner." 
                  title="Branch" 
                  trigger="hover"
                >
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch
              checked={payload.branch}
              onChange={(checked) => {
                setPayload((prev) => ({
                  ...prev,
                  branch: checked,
                }));
              }}
            />
          </Form.Item>

        </Form>
      );
    } else if (currentStep === 1) {
      return <div> <Form layout="vertical">
        <Form.Item label="Email Address" validateStatus={errors.email ? 'error' : ''} help={errors.email || ''}>
          <Input size="large" name="email" value={payload["email"]} onChange={(e) => { setPayload(prev => ({ ...prev, email: e.target.value })); const validateData = { tpemail: e.target.value }; validateFormData(validateData); }} placeholder="Email Address" />
        </Form.Item>

        <Form.Item label="Contact Number" validateStatus={errors.contactNumber ? 'error' : ''} help={errors.contactNumber || ''} required >
          <Input size="large" name="contactNumber" value={payload["contactNumber"]} onChange={(e) => { setPayload(prev => ({ ...prev, contactNumber: e.target.value })); const validateData = { contactNumber: e.target.value }; validateFormData(validateData); }} placeholder="Contact Number" />
        </Form.Item>

        <Form.Item label="Address">
          <Form.Item label="Address Line 1" validateStatus={errors.addressLine1 ? 'error' : ''} help={errors.addressLine1 || ''} required>
            <Input size="large" name="addressLine1" value={payload["addressLine1"]} onChange={(e) => { setPayload(prev => ({ ...prev, addressLine1: e.target.value })); const validateData = { addressLine1: e.target.value }; validateFormData(validateData); }} placeholder="Address Line 1" />
          </Form.Item>

          <Form.Item label="Address Line 2" validateStatus={errors.addressLine2 ? 'error' : ''} help={errors.addressLine2 || ''}>
            <Input size="large" name="addressLine2" value={payload["addressLine2"]} onChange={(e) => { setPayload(prev => ({ ...prev, addressLine2: e.target.value })); const validateData = { addressLine2: e.target.value }; validateFormData(validateData); }} placeholder="Address Line 2" />
          </Form.Item>

          <Form.Item label="Address Line 3" validateStatus={errors.addressLine3 ? 'error' : ''} help={errors.addressLine3 || ''}>
            <Input size="large" name="addressLine3" value={payload["addressLine3"]} onChange={(e) => { setPayload(prev => ({ ...prev, addressLine3: e.target.value })); const validateData = { addressLine3: e.target.value }; validateFormData(validateData); }} placeholder="Address Line 3" />
          </Form.Item>

          <Form.Item label="Postal Zone" validateStatus={errors.postalZone ? 'error' : ''} help={errors.postalZone || ''}>
            <Input size="large" name="postalZone" value={payload["postalZone"]} onChange={(e) => { setPayload(prev => ({ ...prev, postalZone: e.target.value })); const validateData = { postalZone: e.target.value }; validateFormData(validateData); }} placeholder="Postal Zone" />
          </Form.Item>

          <Form.Item label="City Name" validateStatus={errors.city ? 'error' : ''} help={errors.city || ''} required >
            <Input size="large" name="city" value={payload["city"]} onChange={(e) => { setPayload(prev => ({ ...prev, city: e.target.value })); const validateData = { city: e.target.value }; validateFormData(validateData); }} placeholder="City Name" />
          </Form.Item>

          <Form.Item label="State" validateStatus={errors.state ? 'error' : ''} help={errors.state || ''} required>
            <Select
              showSearch
              name="state"
              placeholder="Select State"
              optionFilterProp="label"
              value={payload["stateName"] || "Select State"}
              onChange={OnStateChange}
              size='large'
              options={stateCodes}
              disabled={payload?.countryCode !== "MYS"}
            />
          </Form.Item>
        </Form.Item>
      </Form></div>;
    }
    return null;
  };

  const onDownloadHandler = () => {
    const link = document.createElement("a");
    link.href = excelFile;
    link.download = "Trading Partner Bulk Upload Sample.xlsx"; // specify the filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const onDownloadExcel = () => {
    if (!tradingPartnerBulk || tradingPartnerBulk.length === 0) {
      openNotificationWithIcon('warning', 'No Data', 'No data available to download', 'bottomLeft');
      return;
    }

    try {
      const worksheetData = tradingPartnerReviewRowsToAoA(tradingPartnerBulk);

      // Create a worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Create a workbook and append the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Trading Partners Review");

      // Generate and download the Excel file
      XLSX.writeFile(workbook, "Trading_Partners_Review.xlsx");
      
      openNotificationWithIcon('success', 'Download Successful', 'Excel file downloaded successfully', 'bottomLeft');
    } catch (error) {
      console.error("Error generating Excel file:", error);
      openNotificationWithIcon('error', 'Excel Generation Failed', 'An error occurred while generating the Excel file.', 'bottomLeft');
    }
  }

  const onDownloadJSON = () => {
    if (!tradingPartnerBulk || tradingPartnerBulk.length === 0) {
      openNotificationWithIcon('warning', 'No Data', 'No data available to download', 'bottomLeft');
      return;
    }

    try {
      // Convert the data to JSON string with proper formatting
      const jsonData = JSON.stringify(tradingPartnerBulk, null, 2);
      
      // Create a blob with the JSON data
      const blob = new Blob([jsonData], { type: 'application/json' });
      
      // Create a download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Trading_Partners_Review.json';
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(link.href);
      
      openNotificationWithIcon('success', 'Download Successful', 'JSON file downloaded successfully', 'bottomLeft');
    } catch (error) {
      console.error("Error generating JSON file:", error);
      openNotificationWithIcon('error', 'JSON Generation Failed', 'An error occurred while generating the JSON file.', 'bottomLeft');
    }
  }

  // Share functionality functions
  const onShareClick = () => {
    if (!tradingPartnerBulk || tradingPartnerBulk.length === 0) {
      openNotificationWithIcon('warning', 'No Data', 'No data available to share', 'bottomLeft');
      return;
    }
    setShareVisible(true);
  }

  const onShareClose = () => {
    setShareVisible(false);
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setToInputValue("");
    setCcInputValue("");
    setBccInputValue("");
    shareForm.resetFields();
  }

  const removeEmail = (email, type) => {
    if (type === 'to') {
      setToEmails(toEmails.filter((item) => item !== email));
      shareForm.setFieldsValue({ to: "" });
    } else if (type === 'cc') {
      setCcEmails(ccEmails.filter((item) => item !== email));
      shareForm.setFieldsValue({ cc: "" });
    } else if (type === 'bcc') {
      setBccEmails(bccEmails.filter((item) => item !== email));
      shareForm.setFieldsValue({ bcc: "" });
    }
  }

  const addEmail = (value, type) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = value.split(',').map(email => email.trim()).filter(email => email && emailRegex.test(email));
    
    if (emails.length === 0) {
      openNotificationWithIcon('error', 'Invalid Email', 'Please enter valid email addresses', 'bottomLeft');
      return;
    }

    if (type === 'to') {
      // Filter out duplicates by checking existing emails
      const newEmails = emails.filter(email => !toEmails.includes(email));
      if (newEmails.length > 0) {
        setToEmails([...toEmails, ...newEmails]);
      }
      setToInputValue(""); // Clear the input field
    } else if (type === 'cc') {
      // Filter out duplicates by checking existing emails
      const newEmails = emails.filter(email => !ccEmails.includes(email));
      if (newEmails.length > 0) {
        setCcEmails([...ccEmails, ...newEmails]);
      }
      setCcInputValue(""); // Clear the input field
    } else if (type === 'bcc') {
      // Filter out duplicates by checking existing emails
      const newEmails = emails.filter(email => !bccEmails.includes(email));
      if (newEmails.length > 0) {
        setBccEmails([...bccEmails, ...newEmails]);
      }
      setBccInputValue(""); // Clear the input field
    }
  }

  const getExcelFileAsBlob = async () => {
    const headings = [
      [
        "Code",
        "Branch",
        "Name",
        "TIN Number",
        "ID Type",
        "ID Number",
        "MSIC Code",
        "Business Activity Description",
        "Address Line 1",
        "Address Line 2",
        "Address Line 3",
        "Postal Code",
        "Address City Name",
        "Address State Code",
        "Address Country Code",
        "SST Registration Number",
        "TTX Registration Number",
        "Email Address",
        "Contact Number",
        "Default Classification Code"
      ]
    ];

    try {
      const rows = Array.isArray(tradingPartnerBulk) ? tradingPartnerBulk : [];
      const data = rows.map((rowValue) => [
        rowValue.code || "",  // Changed from companyCode to code
        rowValue.isBranch ? "TRUE" : "FALSE",
        rowValue.tradingPartnerName || "",
        rowValue.tin || "",
        rowValue.registrationType || "",
        rowValue.registrationValue || "",
        rowValue.msic || "",
        rowValue.description || "",
        rowValue.addressLine1 || "",
        rowValue.addressLine2 || "",
        rowValue.addressLine3 || "",
        rowValue.postalCode || "",
        rowValue.addressCityName || "",
        rowValue.addressStateCode || "",
        rowValue.addressCountryCode || "",
        rowValue.sst || "",
        rowValue.ttx || "",
        rowValue.emailAddress || "",
        rowValue.contactNumber || "",
        rowValue.defaultClassificationCode || ""
      ]);

      const worksheetData = [...headings, ...data];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Trading Partners Review");

      // Generate binary string and convert to Blob
      const binaryString = XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
      const buffer = new ArrayBuffer(binaryString.length);
      const view = new Uint8Array(buffer);

      for (let i = 0; i < binaryString.length; i++) {
        view[i] = binaryString.charCodeAt(i) & 0xff;
      }

      return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    } catch (error) {
      console.error("Error generating Excel file:", error);
      throw error;
    }
  }

  const onShareSubmit = async (formData) => {
    if (toEmails.length === 0) {
      openNotificationWithIcon('error', 'Missing Recipients', 'Please add at least one recipient email', 'bottomLeft');
      return;
    }

    setShareLoading(true);
    try {
      // Generate Excel file as blob
      const excelBlob = await getExcelFileAsBlob();
      const excelFile = new File([excelBlob], "Trading_Partners_Review.xlsx", { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      });

      // Create FormData for API call
      const newFormData = new FormData();
      newFormData.append("to", toEmails.join(','));
      newFormData.append("cc", ccEmails.join(','));
      newFormData.append("bcc", bccEmails.join(','));
      newFormData.append("subject", formData.subject || "Trading Partners Review");
      newFormData.append("body", formData.body || "Please find attached the trading partners review data.");
      newFormData.append("attachments", excelFile);

      await DocumentManagementAPI.FileShareEmail(token, newFormData)
        .then((response) => {
          openNotificationWithIcon('success', 'Email Sent', 'Trading partners review has been shared successfully!', 'bottomLeft');
          onShareClose();
        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon('error', 'Failed', 'Email sending failed: ' + err.response.data.message, 'bottomLeft');
          } else {
            openNotificationWithIcon('error', 'Failed', 'Email sending failed: ' + err.message, 'bottomLeft');
          }
        });
    } catch (error) {
      console.error("Error sharing file:", error);
      openNotificationWithIcon('error', 'Share Failed', 'An error occurred while sharing the file.', 'bottomLeft');
    } finally {
      setShareLoading(false);
    }
  }

  const props = {
    name: "file",
    maxCount: 1,
    multiple: false,
    beforeUpload: (file) => {
      if (file.type !== "application/vnd.ms-excel" && file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        message.error(`Please upload an Excel file`);
        setBU_Payload({ "file": {}, "isFileUpload": false }); // No file selected
        handleUploadSuccess(); // deselect the selected previously selected file if exixts
        return Upload.LIST_IGNORE; // Prevent upload
      }
      setBU_Payload({
        "file": file,
        "isFileUpload": true
      });
      setErrors({});
      setIsDragFile(false);
      return false;
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const validateBeforeBulkSubmit = () => {
    let validateData = {};
    if (bu_payload.isFileUpload) {
      validateData = {
        tradingPartnerFile: "True"
      }
    } else {
      validateData = {
        tradingPartnerFile: "False"
      }
    }

    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
        newErrors[value] = validationErr[value];
      })
      setErrors(newErrors);
      return false;
    } else {
      return true;
    }
  }

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result;
        const base64 = result && typeof result === 'string' ? result.split(',')[1] : null;
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const onBulkSubmit = async () => {
    const validationStatus = validateBeforeBulkSubmit();
    if (validationStatus) {
      setLoading(true); // start loading
      const uploadedFile = bu_payload.file;
      const formData = new FormData();
      formData.append("file", uploadedFile);
      await TradingPartnersAPI.csvUpload(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), formData)
        .then(async (response) => {
          const rows = [];
          response.savedRecords.map((rowValue) => {
            rows.push({
              code: rowValue.companyCode || "",
              isBranch: rowValue.isBranch || false,
              tradingPartnerName: rowValue.tradingPartnerName || "",
              tin: rowValue.tin || "",
              registrationType: rowValue.registrationType || "",
              registrationValue: rowValue.registrationValue || "",
              msic: rowValue.msicCode?.code || "",
              description: rowValue.msicCode?.description || "",
              addressLine1: rowValue.address?.AddressLine1 || "",
              addressLine2: rowValue.address?.AddressLine2 || "",
              addressLine3: rowValue.address?.AddressLine3 || "",
              postalCode: rowValue.address?.PostalZone || rowValue.address?.postalZone || "",
              addressCityName: rowValue.address?.City || rowValue.address?.city || "",
              addressStateCode: rowValue.address?.State?.Code || rowValue.address?.state?.code || "",
              addressCountryCode: rowValue.country?.code || "",
              sst: rowValue.sst || "",
              ttx: rowValue.ttx || "",
              emailAddress: rowValue.emailAddress || "",
              contactNumber: rowValue.contactNumber || "",
              defaultClassificationCode: rowValue.classificationCode || "",
              status: "Success",
              errors: rowValue.errors || []
            })
          });

          response.invalidRecords.map((rowValue) => {
            rows.push({
              code: rowValue.companyCode || "",
              isBranch: rowValue.isBranch || false,
              tradingPartnerName: rowValue.tradingPartnerName || "",
              tin: rowValue.tin || "",
              registrationType: rowValue.registrationType || "",
              registrationValue: rowValue.registrationValue || "",
              msic: rowValue.msicCode?.code || "",
              description: rowValue.msicCode?.description || "",
              addressLine1: rowValue.address?.AddressLine1 || "",
              addressLine2: rowValue.address?.AddressLine2 || "",
              addressLine3: rowValue.address?.AddressLine3 || "",
              postalCode: rowValue.address?.PostalZone || rowValue.address?.postalZone || "",
              addressCityName: rowValue.address?.City || rowValue.address?.city || "",
              addressStateCode: rowValue.address?.State?.Code || rowValue.address?.state?.code || "",
              addressCountryCode: rowValue.country?.code || "",
              sst: rowValue.sst || "",
              ttx: rowValue.ttx || "",
              emailAddress: rowValue.emailAddress || "",
              contactNumber: rowValue.contactNumber || "",
              defaultClassificationCode: rowValue.classificationCode || "",
              status: "Fail",
              errors: rowValue.errors || []
            })
          });
          setTradingPartnerBulk(rows);
          if (Array.isArray(response.invalidRecords) && response.invalidRecords.length > 0) {
            // Send error email with the uploaded Excel file as base64
            if (uploadedFile) {
              try {
                const fileBase64 = await fileToBase64(uploadedFile);
                const groupId = localStorage.getItem('groupId');
                const companyId = localStorage.getItem('companyId');
                if (fileBase64 && groupId && companyId) {
                  let errorLogFileBase64 = null;
                  try {
                    const errorLogRows = response.invalidRecords.map(mapApiInvalidRecordToReviewRow);
                    errorLogFileBase64 = buildTradingPartnerReviewWorkbookBase64(errorLogRows);
                  } catch {
                    errorLogFileBase64 = null;
                  }
                  const emailPayload = {
                    fileName: uploadedFile.name,
                    fileBase64,
                  };
                  if (errorLogFileBase64) {
                    emailPayload.errorLogFileName = "Trading_Partner_Error_Log.xlsx";
                    emailPayload.errorLogFileBase64 = errorLogFileBase64;
                  }
                  NotificationAPI.sendTradingPartnerErrorEmail(
                    token,
                    groupId,
                    companyId,
                    emailPayload
                  ).catch(() => {
                    // Silent fail - email notification is optional
                  });
                }
              } catch {
                // Silent fail - email notification is optional
              }
            }

            if (response.savedRecords.length === 0) {
              openNotificationWithIcon('error', 'Trading partner bulk upload failed', "There are Errors in the Uploaded file", 'bottomLeft');
            } else {
              openNotificationWithIcon('success', 'Trading Partner Bulk Upload Partially Successful', "" + response.savedRecords.length + " records uploaded successfully. " + response.invalidRecords.length + " failed. See table for details.", 'bottomLeft');
            }
          }
          else {
            if (response.savedRecords.length === 0 && response.invalidRecords.length === 0) {
              openNotificationWithIcon('error', 'Trading partner bulk upload failed', "There are no records in the uploaded file", 'bottomLeft');
            } else {
              handleUploadSuccess();
              onClose();
              openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
            }

          }

        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon('error', 'Trading partner bulk upload failed', `${err.response.data.message}`, 'bottomLeft');
          } else {
            openNotificationWithIcon('error', 'Trading partner bulk upload failed', `${err.message}`, 'bottomLeft');
          }
        }).finally(() => {
          setLoading(false); // stop loading
        });
    }
  }

  const onBulkClearChange = () => {
    setBU_Payload({
      "file": {},
      "isFileUpload": false
    });
    setErrors({});
    setTradingPartnerBulk([]);
    setIsDragFile(true);
    handleUploadSuccess();
  }

  const onBulkClose = () => {
    setVisible(false);
    setBU_Payload({
      "file": {},
      "isFileUpload": false
    });
    setErrors({});
    setTradingPartnerBulk([]);
    setIsBulkUpload(false);
    setIsDragFile(true);
  }

  const handleChange = (info) => {
    const { fileList, file } = info; // All selected files
    if (fileList.length > 0) {
    } else {
      setIsDragFile(true);
    }

  }

  /* Reset Fields---------------------------------- */

  const renderBulkUploadContent = () => {
    return (
      <>
        <Flex justify='center'>
          <div style={{ width: '500px' }}>
            <Form layout="vertical">
              <Form.Item name="download">
                <Button type="primary" onClick={onDownloadHandler} block size="large">
                  Download Sample File
                </Button>
              </Form.Item>

              <Form.Item label="File" name="file" validateStatus={errors.tradingPartnerFile ? 'error' : ''} help={errors.tradingPartnerFile || ''} required>
                <Upload.Dragger name="file" {...props} key={componentKey} onChange={handleChange}>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint"> Support for a Single Excel file (.xlsx) upload.</p>
                </Upload.Dragger>
              </Form.Item>
            </Form>
          </div>
        </Flex>
        <br />

        {/* Download buttons section */}
        {tradingPartnerBulk && Array.isArray(tradingPartnerBulk) && tradingPartnerBulk.length > 0 && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Space>
              <Button 
                type="default" 
                icon={<FileExcelOutlined />} 
                onClick={onDownloadExcel}
                size="large"
              >
                Download as Excel
              </Button>
              <Button 
                type="default" 
                icon={<FileTextOutlined />} 
                onClick={onDownloadJSON}
                size="large"
              >
                Download as JSON
              </Button>
              <Button 
                type="primary" 
                icon={<ShareAltOutlined />} 
                onClick={onShareClick}
                size="large"
              >
                Share
              </Button>
            </Space>
          </div>
        )}

        <Table
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          columns={mergedColumns}
          dataSource={tradingPartnerBulk}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No records found!' /> }}
          scroll={{
            x: '100%', // Horizontal scrolling
          }}
          bordered
        />
      </>
    );
  };

  const renderFooter = () => {
    if (isBulkUpload) {
      return (
        <div style={{ textAlign: 'right' }}>
          <Button type="default" onClick={onBulkClearChange} size="large" style={{ marginRight: 8 }}>
            Reset
          </Button>
          <Button type="primary"
            disabled={isDragFile}
            onClick={!loading ? onBulkSubmit : null}
            size="large"
            htmlType="submit"
            style={{ marginTop: "15px", cursor: loading ? 'not-allowed' : 'pointer' }}
            icon={loading ? <LoadingOutlined /> : null}
          >
            {loading ? "Submiting..." : "Submit"}
          </Button>
        </div>
      );
    } else {
      return (
        <div style={{ textAlign: 'right' }}>
          <Button style={{ marginRight: 8 }} size="large" onClick={onClearChange}>
            Reset
          </Button>
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} size="large" onClick={prev}>
              Previous
            </Button>
          )}
          {currentStep < 1 ? (
            <Button type="primary" size="large" onClick={next}>
              Next
            </Button>
          ) : (
            <Button type="primary" size="large" onClick={!loading ? onSubmit : null}
              htmlType="submit"
              style={{ marginTop: "15px", cursor: loading ? 'not-allowed' : 'pointer' }}
              icon={loading ? <LoadingOutlined /> : null}
            >
              {loading ? "Submiting..." : "Submit"}
            </Button>
          )}
        </div>
      );
    }
  };

  return (
    <Context.Provider>
      {contextHolder}
      <Drawer
        title={
          <Space>
            <span>Create Trading Partner</span>


          </Space>
        }
        extra={
          <Switch
            style={{ transform: 'scale(1.3)' }}
            checked={isBulkUpload}
            onChange={(checked) => setIsBulkUpload(checked)}
            checkedChildren="Bulk"
            unCheckedChildren="Form"
            size='large'

          />
        }
        width={isBulkUpload ? '100%' : null}
        size={isBulkUpload ? '100%' : 'large'}
        visible={visible}
        onClose={() => onBulkClose()}
        footer={renderFooter()}
      >
        {isBulkUpload ? renderBulkUploadContent() : (
          <>
            <Steps current={currentStep}>
              <Step title="General Information" />
              <Step title="Contact Information" />
            </Steps>
            <div style={{ marginTop: 24 }}>{renderStepContent()}</div>
          </>
        )}
      </Drawer>

      {/* Share Modal */}
      <Drawer
        title="Share Trading Partners Review"
        width={600}
        visible={shareVisible}
        onClose={onShareClose}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} onClick={onShareClose}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              loading={shareLoading}
              onClick={() => shareForm.submit()}
            >
              Send Email
            </Button>
          </div>
        }
      >
        <Form
          form={shareForm}
          layout="vertical"
          onFinish={onShareSubmit}
        >
          <Form.Item label="To" required>
            <div>
              {toEmails.map((email, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeEmail(email, 'to')}
                  style={{ marginBottom: 4 }}
                >
                  {email}
                </Tag>
              ))}
              {toEmails.length > 0 && (
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => setToEmails([])}
                  style={{ padding: '0 4px', height: 'auto', fontSize: '12px', marginBottom: '4px' }}
                >
                  Clear All
                </Button>
              )}
              <Input
                placeholder="Enter email addresses (comma separated)"
                autoComplete="off"
                value={toInputValue}
                onChange={(e) => setToInputValue(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  addEmail(e.target.value, 'to');
                }}
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    addEmail(e.target.value, 'to');
                  }
                }}
                style={{ marginTop: 4 }}
              />
            </div>
          </Form.Item>

          <Form.Item label="CC">
            <div>
              {ccEmails.map((email, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeEmail(email, 'cc')}
                  style={{ marginBottom: 4 }}
                >
                  {email}
                </Tag>
              ))}
              {ccEmails.length > 0 && (
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => setCcEmails([])}
                  style={{ padding: '0 4px', height: 'auto', fontSize: '12px', marginBottom: '4px' }}
                >
                  Clear All
                </Button>
              )}
              <Input
                placeholder="Enter CC email addresses (comma separated)"
                autoComplete="off"
                value={ccInputValue}
                onChange={(e) => setCcInputValue(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  addEmail(e.target.value, 'cc');
                }}
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    addEmail(e.target.value, 'cc');
                  }
                }}
                style={{ marginTop: 4 }}
              />
            </div>
          </Form.Item>

          <Form.Item label="BCC">
            <div>
              {bccEmails.map((email, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeEmail(email, 'bcc')}
                  style={{ marginBottom: 4 }}
                >
                  {email}
                </Tag>
              ))}
              {bccEmails.length > 0 && (
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => setBccEmails([])}
                  style={{ padding: '0 4px', height: 'auto', fontSize: '12px', marginBottom: '4px' }}
                >
                  Clear All
                </Button>
              )}
              <Input
                placeholder="Enter BCC email addresses (comma separated)"
                autoComplete="off"
                value={bccInputValue}
                onChange={(e) => setBccInputValue(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  addEmail(e.target.value, 'bcc');
                }}
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    addEmail(e.target.value, 'bcc');
                  }
                }}
                style={{ marginTop: 4 }}
              />
            </div>
          </Form.Item>

          <Form.Item 
            label="Subject" 
            name="subject"
            initialValue="Trading Partners Review"
          >
            <Input placeholder="Email subject" autoComplete="off" />
          </Form.Item>

          <Form.Item 
            label="Message" 
            name="body"
            initialValue="Please find attached the trading partners review data."
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Email message"
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item label="Attachment">
            <div style={{ 
              padding: '12px', 
              border: '1px dashed #d9d9d9', 
              borderRadius: '6px',
              backgroundColor: '#fafafa'
            }}>
              <FileExcelOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              Trading_Partners_Review.xlsx
            </div>
          </Form.Item>
        </Form>
      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
